import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaPicker } from "@/components/cms/MediaPicker";
import { Palette } from "lucide-react";

const BG_TYPES = [
  { v: "none", label: "None (default)" },
  { v: "color", label: "Solid color" },
  { v: "image", label: "Image" },
  { v: "video", label: "Video URL" },
];
const OVERLAYS = [
  { v: "none", label: "No filter" },
  { v: "teal", label: "Teal / Navy" },
  { v: "red", label: "Red" },
  { v: "gold", label: "Gold (warm)" },
  { v: "dark", label: "Dark (readability)" },
  { v: "light", label: "Light (readability)" },
];
const OPACITIES = [0, 20, 40, 60, 80];

const SWATCH = { none: "transparent", teal: "#1C4358", red: "#9C1E22", gold: "#E0A526", dark: "#0B1F2A", light: "#FFFFFF" };

// Reusable hero-background control. `value` is a hero_background object; `onChange(next)`.
export const BackgroundEditor = ({ value, onChange, testid = "bg-editor" }) => {
  const bg = value || {};
  const type = bg.type || "none";
  const set = (k, v) => onChange({ ...bg, [k]: v });

  return (
    <div className="rounded-xl border border-[#E6EDF2] bg-[#F7FAFC] p-4 space-y-3" data-testid={testid}>
      <p className="text-[14px] font-display font-bold text-[#1C4358] flex items-center gap-1.5">
        <Palette size={16} /> Hero background
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-[12px] font-semibold text-[#1C4358]">Background type</Label>
          <Select value={type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid={`${testid}-type`} className="h-9 text-[14px]"><SelectValue /></SelectTrigger>
            <SelectContent>{BG_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {type === "color" && (
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Color</Label>
            <div className="flex items-center gap-2">
              <input type="color" data-testid={`${testid}-color`} value={bg.color || "#1C4358"}
                onChange={(e) => set("color", e.target.value)}
                className="h-9 w-12 rounded-lg border border-[#1C4358]/20 bg-white cursor-pointer" />
              <Input value={bg.color || "#1C4358"} onChange={(e) => set("color", e.target.value)}
                className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0" />
            </div>
          </div>
        )}
      </div>

      {type === "image" && (
        <MediaPicker label="Background image" testid={`${testid}-image`}
          value={bg.image_url || ""} alt={bg.image_alt || ""}
          onUrlChange={(url) => set("image_url", url)} onAltChange={(alt) => set("image_alt", alt)} />
      )}

      {type === "video" && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Video URL (YouTube, Vimeo, or .mp4)</Label>
            <Input data-testid={`${testid}-video`} value={bg.video_url || ""} placeholder="https://…"
              className="h-9 text-[14px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0"
              onChange={(e) => set("video_url", e.target.value)} />
            <p className="text-[11px] text-[#6B7280]">No autoplay with sound. Direct .mp4 plays muted; YouTube/Vimeo use the poster image below.</p>
          </div>
          <MediaPicker label="Poster image (fallback)" testid={`${testid}-poster`}
            value={bg.poster_url || ""} alt={bg.image_alt || ""}
            onUrlChange={(url) => set("poster_url", url)} onAltChange={(alt) => set("image_alt", alt)} />
        </div>
      )}

      {type !== "none" && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Color overlay (filter)</Label>
            <Select value={bg.overlay || "none"} onValueChange={(v) => set("overlay", v)}>
              <SelectTrigger data-testid={`${testid}-overlay`} className="h-9 text-[14px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OVERLAYS.map((o) => (
                  <SelectItem key={o.v} value={o.v}>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block h-3 w-3 rounded-full border border-black/10" style={{ backgroundColor: SWATCH[o.v] }} />
                      {o.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[12px] font-semibold text-[#1C4358]">Overlay opacity</Label>
            <Select value={String(bg.overlay_opacity ?? 40)} onValueChange={(v) => set("overlay_opacity", parseInt(v, 10))}>
              <SelectTrigger data-testid={`${testid}-opacity`} className="h-9 text-[14px]"><SelectValue /></SelectTrigger>
              <SelectContent>{OPACITIES.map((o) => <SelectItem key={o} value={String(o)}>{o}%</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}
      {type !== "none" && (
        <p className="text-[11px] text-[#6B7280]">Tip: keep text readable — a Dark or Teal overlay at 40–60% works well over busy photos.</p>
      )}
    </div>
  );
};

export default BackgroundEditor;
